import React, { useEffect } from "react";
import { useSearchParams } from "react-router";
import { useSsoCallback } from "../../services/sso/hooks/useSsoCallback";

/**
 * SSO Login Page — handles the return from the Booking Engine.
 *
 * Route: /sso/login?data=...&signature=...
 *
 * Reads `data` and `signature` from URL params, auto-triggers the
 * SSO callback mutation to validate and login the user.
 */
const SsoLoginPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const ssoCallback = useSsoCallback();

    const data = searchParams.get("data");
    const signature = searchParams.get("signature");

    useEffect(() => {
        if (data && signature) {
            ssoCallback.mutate({ data, signature });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, signature]);

    // Missing params
    if (!data || !signature) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="text-center">
                    <h4 className="text-danger">Invalid SSO Request</h4>
                    <p className="text-muted">Missing required parameters (data and signature).</p>
                </div>
            </div>
        );
    }

    // Loading
    if (ssoCallback.isPending) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Signing you in...</p>
                </div>
            </div>
        );
    }

    // Error
    if (ssoCallback.isError) {
        const axiosError = ssoCallback.error;
        const apiMessage = (axiosError?.response?.data as { message?: string })?.message;
        const errorMessage = apiMessage || axiosError?.message || "An error occurred during SSO login.";

        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="text-center">
                    <h4 className="text-danger">Login Failed</h4>
                    <p className="text-muted">{errorMessage}</p>
                    <a href="/login" className="btn btn-primary">
                        Go to Login Page
                    </a>
                </div>
            </div>
        );
    }

    return null;
};

export default SsoLoginPage;
